import React from 'react'
import { useFetchUsersQuery, useUpdateUserMutation } from '../../../../../store/apis/user.api'
import UserForm from '../UserForm'
import { openNotificationWithIcon } from '../../../../../utils/notification.helper'
import { useSelector } from 'react-redux'
import { fetchUsersSelectors } from '../../../../../store/apis/utils/selectors/user.api.selectors'
import userSelectors from '../../../../../store/selectors/user.selectors'
import { useFetchOrganizationsQuery } from '../../../../../store/apis/organization.api'
import useAuthorization from '../../../../../components/authorization/useAuthorization'
import UserRoles from '../../../../../constants/UserRoles'
import { useIsSenseUserOrImpersonated } from '../../../../../hooks/useIsSenseUserOrImpersonated'

const EditUserForm = ({ onSuccess }) => {
    const editingUserId = useSelector(userSelectors.editingUserId)
    const isSenseUserOrImpersonated = useIsSenseUserOrImpersonated()
    const { isAuthorized: isSystemAdmin } = useAuthorization([UserRoles.SYSTEM_ADMIN])
    const { isAuthorized: isAdmin } = useAuthorization([UserRoles.ADMIN])
    const { isAuthorized: isUserManager } = useAuthorization([UserRoles.USER_MANAGER])
    
    // Allow organization assignment for system admins, regular admins, and user managers
    const canAssignOrganization = isSystemAdmin || isAdmin || isUserManager
    
    // Sense users (Admin/User Manager) and System Admins can view all users across organizations
    // External org users (Admin/User Manager) can only view users from their own organization
    const canViewAllUsers = isSystemAdmin || (isSenseUserOrImpersonated && (isAdmin || isUserManager))

    const { data: user } = useFetchUsersQuery(
        { organizationOnly: !canViewAllUsers },
        { selectFromResult: result => fetchUsersSelectors.byId({ id: editingUserId }, result) },
    )

    const [updateUser, updateUserResult] = useUpdateUserMutation()
    const { data: organizations, isFetching: isFetchingOrganizations } = useFetchOrganizationsQuery(null, {
        skip: !canAssignOrganization,
    })

    const handleUpdateUser = async values => {
        const response = await updateUser({ id: user.id, ...values })

        if ('data' in response) {
            openNotificationWithIcon('success', 'Saved successfully')
            onSuccess && onSuccess()
        }
    }

    const fieldsToHide = canAssignOrganization ? ['email', 'password'] : ['email', 'password', 'organizationId']

    return (
        <UserForm
            organizations={organizations}
            isFetchingOrganizations={isFetchingOrganizations}
            onSubmit={handleUpdateUser}
            loading={updateUserResult.isLoading}
            error={updateUserResult.error}
            fieldsToHide={fieldsToHide}
            actionName='Save'
            initialValues={{
                name: user?.name,
                email: user?.email,
                organizationId: user?.organizationId,
                roles: user?.roles,
                status: user?.status,
            }}
        />
    )
}

export default EditUserForm
